import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { errorMessage } from "../../../../redux/actions/messagesActions";
import { getReviewStages, resetReviewStages } from "../../../../redux/actions/reviewStagesActions";
import { errorMessageForDatesValidation } from "../../../../utils/validationUtils";
import { setPermissions } from "../../../../redux/actions/permissionsActions";
import { selectExecutionBriefOwnerId } from "../../../../redux/selectors/executionBriefs";
import { selectWorkOrderOwnerId } from "../../../../redux/selectors/workOrders";
import * as userStore from "../../../../features/user/store/user.store";
import { calculatePermissions } from "../../../../types/user/permissions/ipermissions/ipermissions.functions";
import { CAMPAIGN_TEMPLATE } from "../../../../types/templates/campaign-brief/campaign-brief-template.enum";
import * as store from "../../store/campaign-brief.store";
import {
  createCampaignBriefPermissionsContext,
  getBriefOwnerFullName,
  getNdaDataFromCampaignBrief,
} from "../../types/icampaign-brief-data.functions";
import { getValidationSchema } from "../../types/campaign-brief-form/campaign-brief-validation.schema";
import {
  createCampaignBriefDefaultValues,
  createCampaignBriefForm,
} from "../../types/campaign-brief-form/campaign-brief-form.functions";
import { CampaignBriefViewComponent } from "../campaign-brief-view/campaign-brief-view.component";
import { IExtraAction } from "../../../../features/brief-header/types/iextra-action.interface";
import { CampaignBriefForm } from "../../types/campaign-brief-form/campaign-brief-form.interface";
import { FileDownload } from "../../../../features/file-attachments/file-download/components/file-download/file-download-component";
import { exportBriefEndpoint } from "../../services/campaign-brief.service";

export const CampaignBrief = () => {
  const dispatch = useDispatch();

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [permissionsCreated, setPermissionsCreated] = useState<boolean>(false);

  const brief = useSelector(store.getBrief);
  const user = useSelector(userStore.getUser);
  const errors = useSelector(store.getErrors);
  const formErrors = useSelector(store.getFormErrors);
  const isLoading = useSelector(store.isLoading);
  const executionOwnerId = useSelector(selectExecutionBriefOwnerId);
  const workorderOwnerId = useSelector(selectWorkOrderOwnerId);

  const briefOwner = useMemo(() => getBriefOwnerFullName(brief.briefOwner), [brief]);
  const validationSchema = useMemo(
    () => getValidationSchema(brief.resourcetype as CAMPAIGN_TEMPLATE),
    [brief]
  );

  const ndaData = useMemo(() => getNdaDataFromCampaignBrief(brief), [brief]);
  const defaultValues = useMemo(() => createCampaignBriefDefaultValues(brief), [brief]);

  useEffect(() => {
    setIsDuplicateModalOpen(false);
  }, [id]);

  useEffect(() => {
    return () => {
      store.resetState();
      dispatch(resetReviewStages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      store.fetchBrief(id);
      dispatch(getReviewStages(Number(id), "campaign-briefs"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setPermissionsCreated(false);
  }, [brief]);

  useEffect(() => {
    if (brief && brief.id) {
      const context = createCampaignBriefPermissionsContext(
        brief,
        user,
        executionOwnerId,
        workorderOwnerId
      );

      setPermissionsCreated(true);
      dispatch(setPermissions(calculatePermissions(context)));
    }
  }, [brief, user, dispatch, permissionsCreated, executionOwnerId, workorderOwnerId]);

  const extraActions = (): IExtraAction[] => {
    return [
      {
        action: () => setIsDuplicateModalOpen(true),
        label: "Duplicate",
        show: !ndaData.isUnderNda,
      },
      {
        label: (
          <FileDownload
            url={exportBriefEndpoint(brief.id)}
            authRequired
            saveAsFileName="campaign-brief-export.docx"
          >
            Export
          </FileDownload>
        ),
      },
    ];
  };

  const saveForm = (form: CampaignBriefForm) => {
    setIsSaving(true);
    store.save(form);
  };

  const onSubmit = (data: any) => {
    const datesValidationError = errorMessageForDatesValidation(
      data.startAt,
      data.endAt,
      brief.status,
      data.status
    );

    if (datesValidationError !== null) {
      dispatch(errorMessage(datesValidationError));
      return;
    }

    saveForm(createCampaignBriefForm(brief, brief.name, data));
  };

  const onChangeName = (campaignName: string) => {
    setIsSaving(true);
    store.rename({ id: brief.id, campaignName });
  };

  const onUploadFile = (file: File) => store.uploadAttachmentFile(id, file);

  return (
    <CampaignBriefViewComponent
      brief={brief}
      ndaData={ndaData}
      attachedFiles={useSelector(store.getAttachedFiles)}
      onSubmit={onSubmit}
      onDeleteFile={store.removeAttachedFile}
      onUploadFile={onUploadFile}
      isLoading={isLoading}
      validationSchema={validationSchema}
      isSaving={isSaving}
      setIsSaving={setIsSaving}
      briefOwnerName={briefOwner}
      errors={errors}
      formErrors={formErrors}
      defaultValues={defaultValues}
      extraActions={extraActions()}
      onDuplicateBrief={store.duplicate}
      setDuplicateModalOpen={setIsDuplicateModalOpen}
      isDuplicateModalOpen={isDuplicateModalOpen}
      onChangeName={onChangeName}
    />
  );
};
